import {
  AppBar, Toolbar, Button, Typography, Stack,
  IconButton, Drawer, List, ListItem, ListItemText, Box, alpha, Collapse,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhoneIcon from "@mui/icons-material/Phone";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import brandLogo from "../../LandingPageImg/ayeSalonbackgroundLess.webp";
import useSiteImages from "../../hooks/useSiteImages";

const ROSE      = "#9e4562";
const ROSE_DARK = "#6b2c47";
const ROSE_LIGHT = "#c4466e";

const featureLinks = [
  { label: "Appointment Scheduling", path: "/features/appointment-scheduling/", icon: "📅", desc: "Online & walk-in booking, calendar & reminders" },
  { label: "Staff Management",       path: "/features/staff-management/",       icon: "👥", desc: "Shifts, targets, commissions & performance" },
  { label: "Client Management",      path: "/features/clients-management/",     icon: "🪪", desc: "Profiles, visit history & loyalty points" },
  { label: "Inventory Management",   path: "/features/inventory-management/",   icon: "📦", desc: "Stock tracking, alerts & purchase orders" },
  { label: "Discounts & Offers",     path: "/features/discount-offers/",        icon: "🏷️", desc: "Coupons, flash sales & seasonal deals" },
  { label: "Reports & Analytics",    path: "/features/client-analytics/",       icon: "📊", desc: "Auto-generated sales/GST reports, revenue trends & retention" },
  { label: "Invoice & Billing",      path: "/features/invoice-system/",         icon: "🧾", desc: "GST invoices, UPI/card & WhatsApp receipt" },
  { label: "Membership Plans",       path: "/features/membership-management/",  icon: "💎", desc: "Recurring revenue with tiered memberships" },
  { label: "GST Accounting",         path: "/features/gst-accounting/",         icon: "🏛️", desc: "Auto CGST/SGST split & GSTR-3B ready reports" },
  { label: "Loyalty & Rewards",      path: "/features/loyalty-rewards/",        icon: "🏆", desc: "Points, tiers & referral rewards that retain clients" },
  { label: "Multi-Branch Management", path: "/features/multi-branch-management/", icon: "🏬", desc: "Centralized dashboard for salon chains, branch-level access" },
  { label: "Staff Attendance & Payroll", path: "/features/staff-attendance-payroll/", icon: "⏱️", desc: "Mobile check-in/out, overtime & commission-based payroll" },
  { label: "Expense Management",       path: "/features/expense-management/",       icon: "💰", desc: "Branch-wise expense tracking for real profit visibility" },
];

const resourceLinks = [
  {
    category: "Learn",
    items: [
      { label: "Blog",           path: "/blog/",           icon: "✍️", desc: "Tips & trends for salon owners" },
      { label: "How-To Guides",  path: "/guides/",         icon: "📖", desc: "Step-by-step setup & growth guides" },
      { label: "Feature Videos", path: "/feature-videos/", icon: "🎬", desc: "Animated walkthroughs of every module" },
    ],
  },
  {
    category: "Proof",
    items: [
      { label: "Testimonials",       path: "/testimonials/",         icon: "⭐", desc: "Real results from 500+ salons" },
      { label: "Video Testimonials", path: "/videos/",               icon: "🎥", desc: "Watch salon owners share their story" },
      { label: "Success Story",      path: "/success-story/shine-beauty/", icon: "🏆", desc: "How Shine Beauty doubled revenue" },
    ],
  },
  {
    category: "Tools",
    items: [
      { label: "ROI Calculator", path: "/roi-calculator/",      icon: "🧮", desc: "See your estimated savings in 60 sec" },
      { label: "Mobile App",     path: "/mobile-app/",          icon: "📱", desc: "Run your salon from Android or iPhone" },
      { label: "vs Excel",       path: "/comparison/vs-excel/", icon: "📋", desc: "Why spreadsheets hold you back" },
      { label: "vs Paper",       path: "/comparison/vs-paper/", icon: "📝", desc: "The true cost of manual record-keeping" },
    ],
  },
];

// ── Features mega-menu ────────────────────────────────────────────────────────
const FeaturesMenu = ({ navigate, setOpen, onPanelEnter, onPanelLeave }) => (
  <Box
    onMouseEnter={onPanelEnter}
    onMouseLeave={onPanelLeave}
    sx={{
      position: "absolute",
      top: "calc(100% + 8px)",
      left: "50%",
      transform: "translateX(-50%)",
      width: 620,
      bgcolor: "#fff",
      border: "1px solid rgba(184,92,122,0.14)",
      borderRadius: "16px",
      boxShadow: "0 20px 60px rgba(107,44,71,0.14), 0 4px 16px rgba(107,44,71,0.06)",
      zIndex: 1300,
      overflow: "hidden",
    }}
  >
    {/* Header strip -- outside role="menu": a menu's direct children must be
        menuitem/group/separator, so descriptive text and the "View all" link
        live here instead of inside the menu below. */}
    <Box sx={{
      px: 3, py: 2,
      background: `linear-gradient(135deg, ${alpha(ROSE, 0.06)} 0%, ${alpha(ROSE, 0.02)} 100%)`,
      borderBottom: "1px solid rgba(184,92,122,0.08)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: ROSE, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Platform Features
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: "#8d6b7c", mt: 0.2 }}>
          Everything you need to run a modern salon
        </Typography>
      </Box>
      <Box
        component="button"
        onClick={() => { navigate("/features/"); setOpen(null); }}
        sx={{
          display: "flex", alignItems: "center", gap: 0.5,
          background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
          fontSize: "0.72rem", fontWeight: 700, color: ROSE,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        View all <ArrowForwardIcon sx={{ fontSize: "0.75rem" }} />
      </Box>
    </Box>

    {/* 2-column grid: this is the actual menu -- every direct child is a menuitem */}
    <Box id="features-menu" role="menu" aria-label="Platform Features" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, p: 1.5 }}>
      {featureLinks.map((link) => (
        <Box
          key={link.path}
          component="button"
          role="menuitem"
          onClick={() => { navigate(link.path); setOpen(null); }}
          sx={{
            display: "flex", alignItems: "flex-start", gap: 1.5,
            p: "10px 14px", m: 0.5,
            background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
            borderRadius: "10px", textAlign: "left",
            transition: "background 0.15s",
            "&:hover": { background: alpha(ROSE, 0.06) },
            "&:hover .feat-label": { color: ROSE },
          }}
        >
          <Box sx={{
            width: 36, height: 36, borderRadius: "9px", flexShrink: 0,
            bgcolor: alpha(ROSE, 0.08),
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1rem", mt: 0.1,
          }}>
            {link.icon}
          </Box>
          <Box>
            <Typography className="feat-label" sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#2d1a25", lineHeight: 1.3, transition: "color 0.15s" }}>
              {link.label}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "#8d6b7c", mt: 0.3, lineHeight: 1.4 }}>
              {link.desc}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

// ── Resources mega-menu ───────────────────────────────────────────────────────
const ResourcesMenu = ({ navigate, setOpen, onPanelEnter, onPanelLeave }) => (
  <Box
    onMouseEnter={onPanelEnter}
    onMouseLeave={onPanelLeave}
    sx={{
      position: "absolute",
      top: "calc(100% + 8px)",
      left: "50%",
      transform: "translateX(-50%)",
      width: 640,
      bgcolor: "#fff",
      border: "1px solid rgba(184,92,122,0.14)",
      borderRadius: "16px",
      boxShadow: "0 20px 60px rgba(107,44,71,0.14), 0 4px 16px rgba(107,44,71,0.06)",
      zIndex: 1300,
      overflow: "hidden",
    }}
  >
    {/* Header strip -- outside role="menu", see FeaturesMenu comment above */}
    <Box sx={{
      px: 3, py: 2,
      background: `linear-gradient(135deg, ${alpha(ROSE, 0.06)} 0%, ${alpha(ROSE, 0.02)} 100%)`,
      borderBottom: "1px solid rgba(184,92,122,0.08)",
    }}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: ROSE, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        Resources
      </Typography>
      <Typography sx={{ fontSize: "0.72rem", color: "#8d6b7c", mt: 0.2 }}>
        Guides, proof and tools to help you decide
      </Typography>
    </Box>

    {/* 3-column categories: this is the actual menu. Each category is a role="group"
        (a menu's only valid non-menuitem child) labelled by its own visible heading. */}
    <Box id="resources-menu" role="menu" aria-label="Resources" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", p: 2, gap: 1 }}>
      {resourceLinks.map((group) => (
        <Box key={group.category} role="group" aria-labelledby={`resources-group-${group.category}`}>
          <Typography id={`resources-group-${group.category}`} sx={{
            fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em",
            textTransform: "uppercase", color: "#935066",
            px: 1.5, pb: 1, display: "block",
          }}>
            {group.category}
          </Typography>
          {group.items.map((link) => (
            <Box
              key={link.path}
              component="button"
              role="menuitem"
              onClick={() => { navigate(link.path); setOpen(null); }}
              sx={{
                display: "flex", alignItems: "flex-start", gap: 1.2,
                p: "9px 12px", mb: 0.5, width: "100%",
                background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
                borderRadius: "9px", textAlign: "left",
                transition: "background 0.15s",
                "&:hover": { background: alpha(ROSE, 0.06) },
                "&:hover .res-label": { color: ROSE },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: "8px", flexShrink: 0,
                bgcolor: alpha(ROSE, 0.07),
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.95rem",
              }}>
                {link.icon}
              </Box>
              <Box>
                <Typography className="res-label" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#2d1a25", lineHeight: 1.3, transition: "color 0.15s" }}>
                  {link.label}
                </Typography>
                <Typography sx={{ fontSize: "0.69rem", color: "#8d6b7c", mt: 0.25, lineHeight: 1.4 }}>
                  {link.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  </Box>
);

// ── Nav button style ──────────────────────────────────────────────────────────
const navButtonSx = (active) => ({
  px: 2.5, py: 1,
  cursor: "pointer",
  background: "none",
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.82rem",
  fontWeight: active ? 600 : 500,
  letterSpacing: "0.02em",
  color: active ? ROSE : "#6a4e62",
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  position: "relative",
  transition: "color 0.2s ease",
  "&:hover": { color: ROSE },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -1, left: "50%",
    transform: "translateX(-50%)",
    width: active ? "20px" : "0px",
    height: "2px",
    background: ROSE,
    borderRadius: "2px",
    transition: "width 0.25s ease",
  },
  "&:hover::after": { width: "20px" },
});

// ── Main component ────────────────────────────────────────────────────────────
export default function Header() {
  const siteImages = useSiteImages();
  const logoSrc = siteImages?.brand_logo?.url || brandLogo;
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [scrolled, setScrolled]         = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen]     = useState(null);
  const closeTimer = useRef(null);
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openMenu  = (id) => { clearTimeout(closeTimer.current); setOpenDropdown(id); };
  const closeMenu = ()   => { closeTimer.current = setTimeout(() => setOpenDropdown(null), 120); };
  const cancelClose = () => { clearTimeout(closeTimer.current); };

  const handleNav = (path) => { navigate(path); setDrawerOpen(false); };
  const isActive  = (path) => location.pathname === path || location.pathname.startsWith(path + "/");
  const isExact   = (path) => location.pathname === path;

  const resourcesActive =
    isActive("/guides") || isActive("/blog") || isActive("/feature-videos") ||
    isActive("/videos") || isActive("/testimonials") || isActive("/roi-calculator") ||
    isActive("/comparison") || isActive("/success-story");

  return (
    <Box component="header">
      {/* Announcement bar */}
      <Box sx={{
        background: `linear-gradient(90deg, ${ROSE_DARK} 0%, #7d3554 50%, ${ROSE_DARK} 100%)`,
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        py: 0.75, px: 2, textAlign: "center", position: "relative", zIndex: 1201,
      }}>
        <Typography sx={{ color: "rgba(255,245,248,0.8)", fontSize: { xs: "0.68rem", sm: "0.75rem" }, fontWeight: 400, letterSpacing: "0.06em" }}>
          Trusted by{" "}
          <Box component="span" sx={{ color: "#f5c6d8", fontWeight: 600 }}>500+ salons</Box>
          {" "}across India —{" "}
          <Box component="button" onClick={() => navigate("/pricing/")} sx={{ color: "#fde8dc", textDecoration: "underline", textUnderlineOffset: "2px", cursor: "pointer", background: "none", border: "none", fontSize: "inherit", fontFamily: "inherit", letterSpacing: "inherit", p: 0, fontWeight: 600, "&:hover": { color: "#ffffff" } }}>
            See plans →
          </Box>
        </Typography>
      </Box>

      <AppBar
        component="div"
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: scrolled ? "rgba(253,245,248,0.97)" : "rgba(253,245,248,0.95)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: scrolled ? "1px solid rgba(184,92,122,0.18)" : "1px solid rgba(184,92,122,0.1)",
          transition: "background-color 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease",
          boxShadow: scrolled ? "0 4px 24px rgba(107,44,71,0.08)" : "none",
          overflow: "visible",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            maxWidth: 1200, mx: "auto", width: "100%",
            px: { xs: 2, md: 5 },
            minHeight: { xs: 72, md: 92 },
            display: "flex", alignItems: "center", justifyContent: "space-between",
            overflow: "visible",
          }}
        >
          {/* Logo */}
          <Box
            component="button"
            onClick={() => navigate("/")}
            sx={{ display: "flex", alignItems: "center", cursor: "pointer", background: "none", border: "none", p: 0, flexShrink: 0 }}
            aria-label="AyeSalon home"
          >
            <Box sx={{ transition: "transform 0.2s ease", "&:hover": { transform: "scale(1.02)" } }}>
              <Box component="img" src={logoSrc} alt="AyeSalon salon management software logo" loading="eager" fetchPriority="high" width="200" height="100" sx={{ display: "block", height: { xs: 72, md: 100 }, width: "auto" }} />
            </Box>
          </Box>

          {/* Desktop nav */}
          <Stack component="nav" direction="row" spacing={0} alignItems="center" sx={{ display: { xs: "none", md: "flex" }, position: "relative" }}>

            {/* Features */}
            <Box
              sx={{ position: "relative" }}
              onMouseEnter={() => openMenu("features")}
              onMouseLeave={closeMenu}
            >
              <Box component="button" onClick={() => navigate("/features/")} sx={navButtonSx(isActive("/features"))} aria-haspopup="true" aria-expanded={openDropdown === "features"} aria-controls="features-menu">
                Features
                <ExpandMoreIcon sx={{ fontSize: "0.9rem", transition: "transform 0.2s", transform: openDropdown === "features" ? "rotate(180deg)" : "none" }} />
              </Box>
              {openDropdown === "features" && (
                <FeaturesMenu navigate={navigate} setOpen={setOpenDropdown} onPanelEnter={cancelClose} onPanelLeave={closeMenu} />
              )}
            </Box>

            {/* Simple links */}
            {[{ label: "Pricing", path: "/pricing/" }, { label: "About", path: "/about/" }, { label: "Contact", path: "/contact/" }].map((link) => (
              <Box key={link.path} component="button" onClick={() => navigate(link.path)} sx={navButtonSx(isExact(link.path))} aria-current={isExact(link.path) ? "page" : undefined}>
                {link.label}
              </Box>
            ))}

            {/* Resources */}
            <Box
              sx={{ position: "relative" }}
              onMouseEnter={() => openMenu("resources")}
              onMouseLeave={closeMenu}
            >
              <Box component="button" onClick={() => navigate("/guides/")} sx={navButtonSx(resourcesActive)} aria-haspopup="true" aria-expanded={openDropdown === "resources"} aria-controls="resources-menu">
                Resources
                <ExpandMoreIcon sx={{ fontSize: "0.9rem", transition: "transform 0.2s", transform: openDropdown === "resources" ? "rotate(180deg)" : "none" }} />
              </Box>
              {openDropdown === "resources" && (
                <ResourcesMenu navigate={navigate} setOpen={setOpenDropdown} onPanelEnter={cancelClose} onPanelLeave={closeMenu} />
              )}
            </Box>
          </Stack>

          {/* Desktop CTAs */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
            <IconButton component="a" href="tel:+917090238960" aria-label="Call us" size="small" sx={{ color: "#8c6a80", border: "1.5px solid rgba(184,92,122,0.2)", borderRadius: "6px", p: 1, transition: "all 0.2s ease", "&:hover": { color: ROSE, borderColor: alpha(ROSE, 0.5), background: alpha(ROSE, 0.06) } }}>
              <PhoneIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Button variant="contained" endIcon={<ArrowForwardIcon sx={{ fontSize: "0.85rem !important" }} />} component="a" href="https://ayesalon.com/app" sx={{ fontSize: "0.78rem", px: 2.5, py: 1.1, fontWeight: 700, letterSpacing: "0.02em" }}>
              Login Now
            </Button>
          </Stack>

          {/* Mobile */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "flex", md: "none" } }}>
            <Button size="small" component="a" href="https://ayesalon.com/app" sx={{ fontSize: "0.72rem", px: 1.8, py: 0.7, fontWeight: 700, background: `linear-gradient(135deg, ${ROSE_LIGHT} 0%, ${ROSE_DARK} 100%)`, color: "#fff", borderRadius: "6px" }}>
              Login Now
            </Button>
            <IconButton onClick={() => setDrawerOpen(true)} aria-label="Open menu" sx={{ color: "#6a4e62", p: 0.75 }}>
              <MenuIcon sx={{ fontSize: "1.4rem" }} />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ "aria-label": "Mobile navigation menu", sx: { width: "80vw", maxWidth: 320, background: "#fdf5f8", borderLeft: "1px solid rgba(184,92,122,0.12)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid rgba(184,92,122,0.1)" }}>
          <img src={logoSrc} alt="AyeSalon salon management software logo" width="144" height="72" style={{ display: "block", width: "auto" }} />
          <IconButton onClick={() => setDrawerOpen(false)} size="small" aria-label="Close menu" sx={{ color: "#8c6a80" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <List sx={{ p: 2, overflowY: "auto" }}>
          {/* Features expandable */}
          <ListItem button onClick={() => setMobileOpen(mobileOpen === "features" ? null : "features")} sx={{ py: 1.5, px: 2, mb: 0.5, borderRadius: "6px", background: isActive("/features") ? alpha(ROSE, 0.08) : "transparent", "&:hover": { background: alpha(ROSE, 0.05) } }}>
            <ListItemText primary="Features" primaryTypographyProps={{ fontWeight: isActive("/features") ? 600 : 400, fontSize: "0.9rem", color: isActive("/features") ? ROSE : "#6a4e62" }} />
            <ExpandMoreIcon sx={{ fontSize: "1rem", color: "#8c6a80", transition: "transform 0.2s", transform: mobileOpen === "features" ? "rotate(180deg)" : "none" }} />
          </ListItem>
          <Collapse in={mobileOpen === "features"}>
            <List disablePadding sx={{ pl: 1, mb: 1 }}>
              <ListItem button onClick={() => handleNav("/features")} sx={{ py: 0.8, px: 2, borderRadius: "6px", "&:hover": { background: alpha(ROSE, 0.05) } }}>
                <ListItemText primary="All Features →" primaryTypographyProps={{ fontSize: "0.82rem", color: ROSE, fontWeight: 700 }} />
              </ListItem>
              {featureLinks.map((link) => (
                <ListItem key={link.path} button onClick={() => handleNav(link.path)} sx={{ py: 0.9, px: 2, borderRadius: "6px", "&:hover": { background: alpha(ROSE, 0.05) } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ fontSize: "1rem", width: 22, textAlign: "center" }}>{link.icon}</Box>
                    <Typography sx={{ fontSize: "0.82rem", color: "#6a4e62" }}>{link.label}</Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Collapse>

          {/* Simple links */}
          {[{ label: "Pricing", path: "/pricing/" }, { label: "About", path: "/about/" }, { label: "Contact", path: "/contact/" }].map((link) => (
            <ListItem key={link.path} button onClick={() => handleNav(link.path)} sx={{ py: 1.5, px: 2, mb: 0.5, borderRadius: "6px", background: isExact(link.path) ? alpha(ROSE, 0.08) : "transparent", "&:hover": { background: alpha(ROSE, 0.05) } }}>
              <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: isExact(link.path) ? 600 : 400, fontSize: "0.9rem", color: isExact(link.path) ? ROSE : "#6a4e62" }} />
            </ListItem>
          ))}

          {/* Resources expandable */}
          <ListItem button onClick={() => setMobileOpen(mobileOpen === "resources" ? null : "resources")} sx={{ py: 1.5, px: 2, mb: 0.5, borderRadius: "6px", "&:hover": { background: alpha(ROSE, 0.05) } }}>
            <ListItemText primary="Resources" primaryTypographyProps={{ fontWeight: 400, fontSize: "0.9rem", color: "#6a4e62" }} />
            <ExpandMoreIcon sx={{ fontSize: "1rem", color: "#8c6a80", transition: "transform 0.2s", transform: mobileOpen === "resources" ? "rotate(180deg)" : "none" }} />
          </ListItem>
          <Collapse in={mobileOpen === "resources"}>
            <List disablePadding sx={{ pl: 1, mb: 1 }}>
              {resourceLinks.map((group) => (
                <Box key={group.category}>
                  <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#935066", px: 2, pt: 1.5, pb: 0.5 }}>
                    {group.category}
                  </Typography>
                  {group.items.map((link) => (
                    <ListItem key={link.path} button onClick={() => handleNav(link.path)} sx={{ py: 0.9, px: 2, borderRadius: "6px", "&:hover": { background: alpha(ROSE, 0.05) } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ fontSize: "1rem", width: 22, textAlign: "center" }}>{link.icon}</Box>
                        <Typography sx={{ fontSize: "0.82rem", color: "#6a4e62" }}>{link.label}</Typography>
                      </Box>
                    </ListItem>
                  ))}
                </Box>
              ))}
            </List>
          </Collapse>
        </List>

        <Box sx={{ p: 3, borderTop: "1px solid rgba(184,92,122,0.08)", mt: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Button variant="outlined" fullWidth onClick={() => { window.open("https://ayesalon.com/app", "_blank"); setDrawerOpen(false); }} sx={{ py: 1.3, borderRadius: "6px", borderColor: alpha(ROSE, 0.4), color: ROSE, fontSize: "0.82rem", fontWeight: 600, "&:hover": { borderColor: ROSE, background: alpha(ROSE, 0.05) } }}>
            Log In
          </Button>
          <Button variant="contained" fullWidth endIcon={<ArrowForwardIcon />} onClick={() => { window.dispatchEvent(new Event("open-demo-modal")); setDrawerOpen(false); }} sx={{ py: 1.3, borderRadius: "6px", fontSize: "0.82rem", fontWeight: 700 }}>
            Book a Free Demo
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
